import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-scroll';
import { 
  FaSun, 
  FaWifi, 
  FaChair, 
  FaUtensils, 
  FaRestroom, 
  FaCoffee, 
  FaDoorOpen, 
  FaGolfBall,
  FaCar,
  FaBolt,
  FaUsers,
  FaClock,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import './ConferenceRoom.css';
import IconWrapper from './IconWrapper';

const ConferenceRoom: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef<number>(0);
  const SWIPE_THRESHOLD = 40;
  
  const conferenceImages = [
    { src: "/conference-room/conf1.webp", alt: "Conference Room Interior" },
    { src: "/conference-room/conf2.webp", alt: "Conference Room Setup" },
    { src: "/conference-room/conf3.webp", alt: "Conference Room Meeting" },
    { src: "/conference-room/conf4.webp", alt: "Conference Room Presentation" }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % conferenceImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + conferenceImages.length) % conferenceImages.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current !== null) {
      touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    }
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > SWIPE_THRESHOLD) {
      if (touchDeltaX.current < 0) {
        nextImage();
      } else {
        prevImage();
      }
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === 'Escape') setIsModalOpen(false);
      if (e.key === 'ArrowRight') setModalIndex((p) => (p + 1) % conferenceImages.length);
      if (e.key === 'ArrowLeft') setModalIndex((p) => (p - 1 + conferenceImages.length) % conferenceImages.length);
    };

    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isModalOpen, conferenceImages.length]);

  const amenities = [
    {
      icon: FaSun,
      title: 'Abundant Natural Light',
      description: 'Create an open, energizing atmosphere'
    },
    {
      icon: FaWifi,
      title: 'Fast WiFi & AV Equipment',
      description: 'Projector, screen, and whiteboards ready for presentations'
    },
    {
      icon: FaChair,
      title: 'Flexible Seating',
      description: 'Extra chairs and foldable tables available'
    },
    {
      icon: FaUtensils,
      title: 'Kitchen Access',
      description: 'Coffee, light refreshments, or catering'
    },
    {
      icon: FaRestroom,
      title: 'Convenient Restrooms',
      description: 'Located nearby for easy access'
    },
    {
      icon: FaCoffee,
      title: 'Onsite Café',
      description: 'Easy access to snacks and beverages'
    },
    {
      icon: FaDoorOpen,
      title: 'Direct Outdoor Access',
      description: 'Fresh-air breaks or quick team huddles'
    },
    {
      icon: FaGolfBall,
      title: 'Outdoor Putting Green',
      description: 'Great way to recharge during breaks'
    }
  ];

  const features = [
    {
      icon: FaUsers,
      title: 'Up to 67 People',
      description: 'Spacious room for large teams'
    },
    {
      icon: FaClock,
      title: 'Hourly Rentals',
      description: 'Flexible booking options'
    }
  ];

  const parkingFeatures = [
    {
      icon: FaCar,
      title: 'Free Onsite Parking',
      description: 'Dedicated lot with ample spaces'
    },
    {
      icon: FaBolt,
      title: 'EV Charging Stations',
      description: 'Electric vehicle charging available'
    }
  ];

  return (
    <section id="conference-room" className="conference-room">
      <div className="container">
        <div className="section-header">
          <h2>Hourly Conference Room Rentals</h2>
          <p>Bright, Fully-Equipped Conference Room for Productive Team Meetings</p>
        </div>

        <div className="conference-hero">
          <div className="conference-images">
            <div className="custom-carousel">
              <button className="carousel-btn prev-btn" onClick={prevImage}>
                <IconWrapper icon={FaChevronLeft} />
              </button>
              <div
                className="carousel-image-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img 
                  src={conferenceImages[currentImageIndex].src} 
                  alt={conferenceImages[currentImageIndex].alt} 
                  className="carousel-image"
                  onClick={() => { setIsModalOpen(true); setModalIndex(currentImageIndex); }}
                />
              </div>
              <button className="carousel-btn next-btn" onClick={nextImage}>
                <IconWrapper icon={FaChevronRight} />
              </button>
              <div className="carousel-dots">
                {conferenceImages.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <div className="conference-info">
            <div className="conference-description">
              <p>
                This professional, sunlit conference room is perfect for hosting team meetings, 
                strategy sessions, offsites, or client presentations. With space for up to 67 people, 
                the room offers a clean and flexible layout designed to support focused collaboration 
                and productivity.
              </p>
            </div>

            <div className="conference-features">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon">
                    <IconWrapper icon={feature.icon} />
                  </div>
                  <div className="feature-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="conference-cta">
              <Link to="contact" smooth={true} duration={500} className="book-conference-btn">
                Book Conference Room
              </Link>
              <p className="booking-note">Reservation required • Professional use only</p>
            </div>
          </div>
        </div>

        {isModalOpen && (
          <div className="image-modal" role="dialog" aria-modal="true">
            <button className="modal-close" aria-label="Close" onClick={() => setIsModalOpen(false)}>×</button>
            <button className="modal-nav prev" onClick={() => setModalIndex((p) => (p - 1 + conferenceImages.length) % conferenceImages.length)}>
              <IconWrapper icon={FaChevronLeft} />
            </button>
            <div
              className="modal-image-container"
              onClick={() => setIsModalOpen(false)}
            >
              <img
                src={conferenceImages[modalIndex].src}
                alt={conferenceImages[modalIndex].alt}
                className="modal-image"
              />
            </div>
            <button className="modal-nav next" onClick={() => setModalIndex((p) => (p + 1) % conferenceImages.length)}>
              <IconWrapper icon={FaChevronRight} />
            </button>
          </div>
        )}

        <div className="amenities-section">
          <h3>Amenities Included</h3>
          <div className="amenities-grid">
            {amenities.map((amenity, index) => (
              <div key={index} className="amenity-card">
                <div className="amenity-icon">
                  <IconWrapper icon={amenity.icon} />
                </div>
                <div className="amenity-content">
                  <h4>{amenity.title}</h4>
                  <p>{amenity.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="parking-section">
          <h3>Parking & Access</h3>
          <div className="parking-grid">
            {parkingFeatures.map((feature, index) => (
              <div key={index} className="parking-card">
                <div className="parking-icon">
                  <IconWrapper icon={feature.icon} />
                </div>
                <div className="parking-content">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="parking-description">
            Enjoy the convenience of free onsite parking, with EV chargers available for electric vehicles. 
            No need to worry about finding a spot or paying extra fees for parking—our dedicated lot ensures 
            a hassle-free arrival for you and your guests.
          </p>
        </div>

        <div className="use-cases">
          <h3>Perfect For</h3>
          <div className="use-cases-list">
            <div className="use-case">Team Meetings</div>
            <div className="use-case">Strategy Sessions</div>
            <div className="use-case">Company Offsites</div>
            <div className="use-case">Client Presentations</div>
            <div className="use-case">Workshops</div>
            <div className="use-case">Training Sessions</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConferenceRoom;
